import _ from 'lodash'
import { generatePath, matchPath, PathMatch } from 'react-router-dom'
import { TFunction } from 'react-i18next'
import { Routes as BriqCommonRouteUtils } from '@briq/shared'
import { getContext } from 'redux-saga/effects'
import { i18n } from 'i18next'

import { Routes } from '../routes'
import { RouteConfig } from '../routes/routes'

import { Dictionary } from './types'

export type MatchedRoute = {
  routeData: PathMatch<string> | null
  key: Routes | string
}

export function parseGetParams(query: string) {
  return BriqCommonRouteUtils.parseGetParams(query)
}

export function stringifyGetParams(query: object) {
  return BriqCommonRouteUtils.stringifyGetParams(query)
}

export function formatUrl(base: string, path?: string, params?: Dictionary<unknown>) {
  return BriqCommonRouteUtils.formatUrl(base, path, params)
}

export function formatPath(path?: string, params?: Dictionary<unknown>) {
  return BriqCommonRouteUtils.formatPath(path, params)
}

export function generateRoute(
  to: Routes | string,
  language: string,
  t: TFunction<'routes'[], undefined>,
  params: Dictionary<React.ReactText> = {},
  withoutTranslation = false,
  query: Dictionary<string> = {}
) {
  let route: string = to

  if (!withoutTranslation) {
    let localizedUrl = !withoutTranslation ? t(`routes:${to}`) : to
    if (!_.startsWith(localizedUrl, '/')) localizedUrl = `/${localizedUrl}`

    route = `/${language}${localizedUrl}`
  }
  const queryString = stringifyGetParams(query)
  return `${generatePath(route, params)}${queryString ? '?' + queryString : ''}`
}

export function findCurrentRoute(routes: RouteConfig, pathname: string, t: TFunction, currentLanguage: string) {
  let notExactMatch: MatchedRoute | null = null
  let exactMatch: MatchedRoute | null = null

  _.forEach(_.keys(routes), key => {
    const config = routes[key]
    if (!config) return true

    const { localizationKey, sensitive } = config
    let path = `/${currentLanguage}`
    if (localizationKey) {
      path += t(localizationKey)
    } else {
      path += t(key)
    }

    const matchedRoute = matchPath(
      {
        path,
        caseSensitive: sensitive,
        end: true // This replaces the `exact` option
      },
      pathname
    )

    if (!matchedRoute) {
      return true
    }

    if (pathname === matchedRoute.pathname) {
      exactMatch = {
        key,
        routeData: matchedRoute
      }
      return false
    } else {
      notExactMatch = {
        key,
        routeData: matchedRoute
      }
    }
    return true
  })
  return exactMatch || notExactMatch
}

export function* translateCurrentRoute(preferedLanguage: string) {
  const i18n: i18n = yield getContext('i18n')
  const routes: RouteConfig = yield getContext('routes')
  const tr = i18n.getFixedT(preferedLanguage, 'routes')

  const currentRoute: MatchedRoute = yield findCurrentRoute(
    routes,
    location.pathname,
    i18n.getFixedT(i18n.language, 'routes'),
    i18n.language
  )

  const newRoute = currentRoute?.key || Routes.Home
  const params = _.pickBy(currentRoute?.routeData?.params, _.identity) as Dictionary<React.ReactText>
  return generateRoute(newRoute, preferedLanguage, tr, params)
}
